// Runtime por aba: contexto de "aba ativa" + hooks que as telas usam para pausar trabalho em segundo
// plano, registrar alterações não salvas e proteger navegações destrutivas. Fora de uma aba (ex.: testes
// de unidade de página), o default `active = true` mantém o comportamento original.
import { createContext, useContext, useEffect, useRef } from "react";
import { useAdminTabs } from "@/store/useAdminTabs";
import { tabKeyOf } from "./tabModel";

export type TabRuntime = {
  tabKey: string;
  active: boolean;
};

export const TabRuntimeContext = createContext<TabRuntime>({ tabKey: "", active: true });

/** True quando a aba desta subárvore está ativa (visível). Default true fora de abas. */
export function useTabActive(): boolean {
  return useContext(TabRuntimeContext).active;
}

/**
 * Intervalo que SÓ roda com a aba ativa E a janela do navegador visível. Em aba inativa o efeito é
 * desmontado (timer limpo) e recriado ao reativar — um único timer, sem duplicação. O callback é lido via
 * ref para não recriar o intervalo a cada render.
 */
export function useActiveInterval(callback: () => void, ms: number): void {
  const active = useTabActive();
  const cbRef = useRef(callback);
  cbRef.current = callback;

  useEffect(() => {
    if (!active) return;
    const tick = () => {
      if (typeof document === "undefined" || document.visibilityState === "visible") cbRef.current();
    };
    const id = window.setInterval(tick, ms);
    return () => window.clearInterval(id);
  }, [active, ms]);
}

/**
 * Opções de refetch do TanStack Query cientes da aba: numa aba INATIVA desliga o refetch disparado por
 * foco da janela e por reconexão (consulta desnecessária em segundo plano). Quando a aba fica ATIVA as
 * regras normais voltam a valer — sem forçar fetch extra aqui (nada é disparado só por alternar), então
 * não há duplicação de requisições. Espalhe no objeto de `useQuery`:
 *   useQuery({ queryKey, queryFn, ...useTabQueryRefetch() })
 */
export function useTabQueryRefetch(): { refetchOnWindowFocus: boolean; refetchOnReconnect: boolean } {
  const active = useTabActive();
  return { refetchOnWindowFocus: active, refetchOnReconnect: active };
}

/** Registra/desfaz o estado "alterações não salvas" desta aba no store, limpando ao desmontar. */
export function useTabDirty(dirty: boolean): void {
  const { tabKey } = useContext(TabRuntimeContext);
  const setDirty = useAdminTabs((s) => s.setDirty);

  useEffect(() => {
    setDirty(tabKey, dirty);
  }, [tabKey, dirty, setDirty]);

  useEffect(() => {
    return () => setDirty(tabKey, false);
  }, [tabKey, setDirty]);
}

/**
 * Substitui o título da aba desta subárvore pelo rótulo dinâmico informado (ex.: nome da entidade
 * carregada, no lugar do rótulo genérico derivado da rota). `null`/vazio é ignorado — mantém o título
 * padrão. Fora de uma aba (testes de página) não faz nada.
 */
export function useTabTitle(title: string | null | undefined): void {
  const { tabKey } = useContext(TabRuntimeContext);
  const setTitle = useAdminTabs((s) => s.setTitle);

  useEffect(() => {
    if (tabKey && title) setTitle(tabKey, title);
  }, [tabKey, title, setTitle]);
}

/**
 * Executa uma ação destrutiva DENTRO da própria aba (ex.: resetar/trocar o registro carregado), pedindo
 * confirmação quando a aba está suja. Protege a "substituição de formulário na mesma aba"; trocar/abrir
 * ABAS preserva o estado e NÃO passa por aqui.
 */
export function useGuardedAction(): (action: () => void) => void {
  const { tabKey } = useContext(TabRuntimeContext);
  const isDirty = useAdminTabs((s) => s.isDirty);

  return (action: () => void) => {
    if (isDirty(tabKeyOf(tabKey))) {
      const ok = window.confirm("Há alterações não salvas nesta aba. Deseja descartá-las e continuar?");
      if (!ok) return;
    }
    action();
  };
}
